import type { LoaderFunctionArgs, LinksFunction, MetaFunction } from "react-router";
import { redirect, useNavigate } from "react-router";
import { login } from "../../shopify.server";
import { useState } from "react";
import landingStyles from "./styles.css?url";
import {
  Globe,
  Zap,
  BarChart3,
  RefreshCw,
  Settings,
  Brain,
  ChevronRight,
  Check,
  Rocket,
  Sparkles
} from "lucide-react";


export const links: LinksFunction = () => [
  { rel: "stylesheet", href: landingStyles },
];

export const meta: MetaFunction = () => [
  { title: "LinguaFlow — AI-Powered Translation for Shopify Stores" },
  {
    name: "description",
    content:
      "Automatically translate your Shopify store into 100+ languages with AI-powered translations. Products, collections, pages, articles, blogs and menus — synced to every market.",
  },
  // The marketing site (www.lingua-flow.org) is the canonical home for this
  // content; point search engines there to avoid duplicate-content dilution.
  { tagName: "link", rel: "canonical", href: "https://www.lingua-flow.org/" },
  { property: "og:title", content: "LinguaFlow — AI-Powered Translation for Shopify Stores" },
  { property: "og:type", content: "website" },
  { property: "og:url", content: "https://www.lingua-flow.org/" },
];

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);

  if (url.searchParams.get("shop")) {
    throw redirect(`/app?${url.searchParams.toString()}`);
  }

  return { showForm: Boolean(login) };
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [isInstalling, setIsInstalling] = useState(false);

  const handleInstall = () => {
    setIsInstalling(true);
    // Redirect to Shopify OAuth flow
    // Get shop parameter from URL if provided, otherwise prompt for it
    const shop = new URLSearchParams(window.location.search).get('shop');
    if (shop) {
      window.location.href = "/auth?shop=" + shop;
    } else {
      // If no shop parameter, we could redirect to a page where they enter their shop
      // For now, we'll just redirect to auth which will handle this
      window.location.href = "/auth";
    }
  };

  return (
    <div className="linguaflow-landing-page">
      {/* Header */}
      <header className="linguaflow-landing-header">
        <nav className="linguaflow-landing-nav">
          <div className="linguaflow-logo-section">
            <div className="linguaflow-logo-icon">
              <img src="/logo.png" alt="LinguaFlow" />
            </div>
            <span className="linguaflow-logo-text">LinguaFlow</span>
          </div>
          <div className="linguaflow-powered-by">
            <Sparkles size={16} />
            <span>Powered by AI</span>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="linguaflow-hero-section">
        <div className="linguaflow-hero-content">
          <div className="linguaflow-coming-soon-banner">
            <div className="linguaflow-badge-pulse"></div>
            <Rocket size={16} style={{ color: '#667eea' }} />
            <span className="linguaflow-badge-text">Ready to Install</span>
          </div>

          <h1 className="linguaflow-hero-title">
            Translate Your Shopify Store <span className="linguaflow-gradient-text">Instantly</span>
          </h1>
          <p className="linguaflow-hero-subtitle">
            AI-powered translations for your entire store. Reach global customers with accurate,
            context-aware translations in 100+ languages. No technical knowledge required.
          </p>

          <button
            className="linguaflow-install-button"
            onClick={handleInstall}
            disabled={isInstalling}
          >
            {isInstalling ? (
              <>
                <RefreshCw size={20} className="linguaflow-animate-spin" />
                Installing...
              </>
            ) : (
              <>
                Install LinguaFlow Now
                <ChevronRight size={20} className="linguaflow-button-icon" />
              </>
            )}
          </button>

          <div className="linguaflow-hero-stats">
            <div className="linguaflow-stat">
              <span className="linguaflow-stat-number">19+</span>
              <span className="linguaflow-stat-label">Resource Types</span>
            </div>
            <div className="linguaflow-stat">
              <span className="linguaflow-stat-number">100+</span>
              <span className="linguaflow-stat-label">Languages</span>
            </div>
            <div className="linguaflow-stat">
              <span className="linguaflow-stat-number">1000s</span>
              <span className="linguaflow-stat-label">Translations/Min</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="linguaflow-features-section">
        <div className="linguaflow-container">
          <h2 className="linguaflow-section-title">Everything You Need to Go Global</h2>
          <div className="linguaflow-features-grid">
            <div className="linguaflow-feature-card">
              <div className="linguaflow-feature-icon">
                <Globe size={24} />
              </div>
              <h3>Global Reach</h3>
              <p>Translate your store into 100+ languages with enterprise-grade AI translation.</p>
            </div>
            <div className="linguaflow-feature-card">
              <div className="linguaflow-feature-icon">
                <Brain size={24} />
              </div>
              <h3>AI-Powered</h3>
              <p>Context-aware translations that understand your products and brand voice.</p>
            </div>
            <div className="linguaflow-feature-card">
              <div className="linguaflow-feature-icon">
                <Zap size={24} />
              </div>
              <h3>Bulk Operations</h3>
              <p>Translate thousands of products, collections, and pages in minutes.</p>
            </div>
            <div className="linguaflow-feature-card">
              <div className="linguaflow-feature-icon">
                <BarChart3 size={24} />
              </div>
              <h3>Real-Time Tracking</h3>
              <p>Monitor translation progress with detailed analytics and reports.</p>
            </div>
            <div className="linguaflow-feature-card">
              <div className="linguaflow-feature-icon">
                <RefreshCw size={24} />
              </div>
              <h3>Auto Sync</h3>
              <p>Keep translations in sync with your Shopify store automatically.</p>
            </div>
            <div className="linguaflow-feature-card">
              <div className="linguaflow-feature-icon">
                <Settings size={24} />
              </div>
              <h3>Smart Configuration</h3>
              <p>Customize what to translate and how with intelligent settings.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="linguaflow-how-it-works">
        <div className="linguaflow-container">
          <h2 className="linguaflow-section-title">Get Started in Minutes</h2>
          <div className="linguaflow-steps">
            <div className="linguaflow-step">
              <div className="linguaflow-step-number">1</div>
              <h3>Install the App</h3>
              <p>Click the install button above to add LinguaFlow to your Shopify store.</p>
            </div>
            <div className="linguaflow-step">
              <div className="linguaflow-step-number">2</div>
              <h3>Select Languages</h3>
              <p>Choose target languages from your Shopify Markets configuration.</p>
            </div>
            <div className="linguaflow-step">
              <div className="linguaflow-step-number">3</div>
              <h3>Start Translating</h3>
              <p>Select resources and begin translating immediately. No setup required!</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="linguaflow-cta-section">
        <div className="linguaflow-container">
          <h2>Ready to Reach Global Customers?</h2>
          <p>Join thousands of Shopify merchants expanding their reach worldwide.</p>
          <button
            className="linguaflow-secondary-button"
            onClick={handleInstall}
            disabled={isInstalling}
          >
            {isInstalling ? (
              <>
                <RefreshCw size={20} className="linguaflow-animate-spin" />
                Installing...
              </>
            ) : (
              <>
                Install LinguaFlow Free
                <ChevronRight size={20} className="linguaflow-button-icon" />
              </>
            )}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="linguaflow-landing-footer">
        <div className="linguaflow-footer-content">
          <div className="linguaflow-footer-links">
            <a href="https://www.lingua-flow.org/en/features" target="_blank" rel="noopener noreferrer">Features</a>
            <a href="https://www.lingua-flow.org/en/pricing" target="_blank" rel="noopener noreferrer">Pricing</a>
            <a href="https://www.lingua-flow.org/en/docs" target="_blank" rel="noopener noreferrer">Documentation</a>
            <a href="https://www.lingua-flow.org/en/support" target="_blank" rel="noopener noreferrer">Support</a>
            <a href="https://www.lingua-flow.org/en/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
            <a href="https://www.lingua-flow.org/en/terms" target="_blank" rel="noopener noreferrer">Terms</a>
          </div>
          <div className="linguaflow-footer-bottom">
            <p>© {new Date().getFullYear()} LinguaFlow. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}